import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { PageMeta, Reveal, SectionHead } from "@/components/shared";

const SERVICES = [
  {
    num: "01",
    title: "Site HSE Leadership for Complex Projects",
    desc: "Keep complex construction, engineering, manufacturing and data centre projects compliant and moving, with experienced HSE leadership on the ground.",
    items: [
      "Site HSE management",
      "HSE leadership",
      "Inspections",
      "Stakeholder coordination",
      "HSE reporting",
      "Workforce engagement",
      "KPI management",
    ],
  },
  {
    num: "02",
    title: "Risk Management for High-Risk Activities",
    desc: "Practical, review-ready risk assessments, method statements and permit systems that control risk at source without slowing the job.",
    items: [
      "Risk assessments",
      "Method statements",
      "Safe systems of work",
      "High-risk activity controls",
      "Permit-to-work support",
      "Task-specific risk controls",
    ],
  },
  {
    num: "03",
    title: "Contractor Assurance You Can Rely On",
    desc: "Confidence that every contractor on your site is competent, inducted and working to agreed HSE standards — from evaluation to corrective actions.",
    items: [
      "Contractor evaluation",
      "Contractor onboarding",
      "HSE requirements",
      "Site monitoring",
      "Performance reviews",
      "Audits",
      "Corrective actions",
    ],
  },
  {
    num: "04",
    title: "Auditing & Inspections That Protect Your Programme",
    desc: "Independent audits, inspections and compliance reviews with tracked corrective actions that protect your programme and compliance record.",
    items: [
      "HSE audits",
      "Site inspections",
      "Compliance reviews",
      "Management-system audits",
      "Corrective-action tracking",
      "KPI / performance reviews",
    ],
  },
  {
    num: "05",
    title: "HSE Management Systems Teams Actually Use",
    desc: "Policies, procedures and systems aligned to ISO 9001 and ISO 14001 — usable on the ground and ready for client and certification audits.",
    items: [
      "HSE policies and procedures",
      "Management systems",
      "Audit programmes",
      "Monitoring",
      "Incident reporting",
      "Environmental management",
      "ISO 9001",
      "ISO 14001",
      "ISO 45001",
    ],
  },
  {
    num: "06",
    title: "Incident Investigation That Stops Repeat Issues",
    desc: "Root-cause analysis, inductions, toolbox talks and training that turn incidents into improvements and keep KPIs moving the right way.",
    items: [
      "Site inductions",
      "Toolbox talks",
      "HSE training",
      "Competency support",
      "Incident investigation",
      "Root-cause analysis",
      "Corrective actions",
      "Lessons learned",
      "Continual improvement",
    ],
  },
];

const RISK_AREAS = [
  "CDM",
  "Work at Height",
  "MEWPs",
  "LOLER",
  "Lifting",
  "Fire Safety",
  "Asbestos",
  "COSHH",
  "LOTO",
  "Electrical Safety",
  "Process Plant",
  "Automation",
];

export default function Services() {
  return (
    <>
      <PageMeta
        title="HSE & SHEQ Services for Complex Projects"
        description="Site HSE leadership, risk management, contractor assurance, audits, management systems and incident investigation for high-risk projects."
      />

      <section className="border-b border-brand-line bg-white py-14 lg:py-20">
        <div className="wrap">
          <Reveal>
            <span className="eyebrow">Our Services</span>
            <h1 className="mt-5 max-w-3xl font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">
              HSE &amp; SHEQ Services
            </h1>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-brand-charcoal/85 sm:text-lg">
              Whatever your environment, the outcome is the same: risks
              controlled, compliance evidenced and work kept moving.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24">
        <div className="wrap grid gap-6 lg:grid-cols-2">
          {SERVICES.map((s, i) => (
            <Reveal key={s.num} delay={(i % 2) * 0.07}>
              <div className="card h-full p-8 transition-all duration-200 hover:border-brand-green/40 hover:shadow-lg hover:shadow-brand-navy/5">
                <span className="font-heading text-sm font-extrabold tracking-widest text-brand-green">
                  {s.num}
                </span>
                <h2 className="mt-3 font-heading text-xl font-bold leading-snug">{s.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-brand-charcoal/80">{s.desc}</p>
                <ul className="mt-5 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                  {s.items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-[13px] leading-snug text-brand-charcoal/90">
                      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-green" />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>

      <div className="wrap">
        <Reveal className="mt-10 max-w-4xl">
          <p className="text-xs leading-relaxed text-brand-charcoal/60">
            References to ISO 9001, ISO 14001 and ISO 45001 reflect working
            knowledge of these systems and their requirements.
            SafetySymbiosis is not itself ISO-certified or UKAS-accredited.
          </p>
        </Reveal>
      </div>
    </section>

      <section className="bg-brand-mist py-14 lg:py-20">
        <div className="wrap">
          <SectionHead
            eyebrow="Specialist Risk Areas"
            title="Specialist Risk Areas"
            intro="Deep familiarity with the activities that demand the most control."
          />
          <Reveal className="mt-8 flex flex-wrap gap-2.5" delay={0.1}>
            {RISK_AREAS.map((r) => (
              <span
                key={r}
                className="rounded-full border border-brand-line bg-white px-4 py-2 text-[13px] font-semibold text-brand-navy transition-colors hover:border-brand-green hover:text-brand-greendeep"
                data-testid={`risk-area-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                {r}
              </span>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-navy py-14 lg:py-20">
        <div className="wrap flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
          <Reveal>
            <h2 className="font-heading text-2xl font-bold text-white sm:text-3xl">
              Not sure which support you need?
            </h2>
            <p className="mt-2 text-sm text-white/70 sm:text-base">
              Tell us about your project or workplace and we will suggest a
              proportionate approach.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/contact" className="btn-green" data-testid="services-cta-contact">
              Get in Touch
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
